/**
 * The top of the patient panel for an HRT / TRT patient, as drawn in "HRT-TRT Patient View - screenshot.png": the
 * lavender program box ("HORMONE PROGRAM · MEN'S TRT", then the patient's goal), the symptom chips under it, and
 * the Patient status box (Programme, Track, First labs, Check-ins, Lab alerts).
 *
 * Every value is the API's (`GET /hrt/patients/:id`): the goal is the intake's own answer without " (HRT / TRT)",
 * the chips are the engine's confirmed findings and severe symptoms, the status lines are services/hrt.service.ts.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { IconCalendar } from './icons';
import { Button } from './ui';
import { CrosshairGlyph } from './HrtUi';
import { dueWindow, shortDay, type HrtPatientView, type ProgramPatientRow } from '../lib/hrt';

/**
 * The hormone program on the patient record (/patients/:id): the track, the intake goal, where the patient is,
 * and the way into the HRT / TRT plan, the same place a Physician Alert for this patient opens.
 */
export function HrtProgramBanner({ row }: { row: ProgramPatientRow }) {
  const workspace = `/patients/${row.patientId}/workspace`;
  const where = row.startedOn
    ? `On hormones since ${shortDay(row.startedOn)}`
    : row.planReleased
      ? 'Plan sent, waiting for the patient'
      : row.latestChoice === 'selected'
      ? 'Pathway chosen, not started'
      : row.latestChoice === 'no_pathway'
        ? 'No hormone pathway recorded'
        : 'Waiting for a hormone decision';
  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-plum-line bg-plum-soft px-4 py-3" data-testid="hrt-program-banner">
      <span className="flex shrink-0 max-sm:hidden">
        <CrosshairGlyph size={34} />
      </span>
      <div className="min-w-0 flex-1 basis-[220px]">
        <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-plum">Hormone program · {row.trackLabel}</p>
        <p className="mt-0.5 font-display text-[20px] leading-tight text-plum-ink" style={{ fontWeight: 600 }}>
          {row.goal ?? 'No goal recorded yet'}
        </p>
        <p className="mt-1 text-[13px] text-plum-ink">
          {where}
          {row.openAlerts > 0 && (
            <>
              {' · '}
              <Link to={`${workspace}?tab=checkins`} className="font-semibold text-alert underline-offset-2 hover:underline" data-testid="hrt-banner-alerts">
                {row.openAlerts} check-in {row.openAlerts === 1 ? 'alert' : 'alerts'} open
              </Link>
            </>
          )}
        </p>
      </div>
      <Link to={workspace} data-testid="open-hrt-plan">
        <Button size="sm">Open HRT / TRT plan</Button>
      </Link>
    </div>
  );
}

/** The lavender goal box. */
export function HrtGoalBox({ trackLabel, goal }: { trackLabel: string | null; goal: string | null }) {
  return (
    <span
      className="inline-flex min-w-0 max-w-full items-center gap-3 rounded-lg bg-plum-soft py-2.5 pl-3 pr-5 sm:max-w-[400px]"
      data-testid="hrt-goal-box"
    >
      <span className="flex shrink-0 max-sm:hidden">
        <CrosshairGlyph size={40} />
      </span>
      <span className="flex shrink-0 sm:hidden">
        <CrosshairGlyph size={30} />
      </span>
      <span className="min-w-0 leading-none">
        <span className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-plum sm:text-[12.5px]">
          Hormone program
          {trackLabel && (
            <>
              {' · '}
              <span className="whitespace-nowrap">{trackLabel}</span>
            </>
          )}
        </span>
        <span
          className="mt-1.5 block font-display text-[22px] leading-[1.08] text-plum-ink [hyphens:auto] sm:text-[28px]"
          style={{ fontWeight: 600 }}
        >
          {goal ?? 'No goal recorded yet'}
        </span>
      </span>
    </span>
  );
}

/** The chips under the goal: "Low testosterone, confirmed twice", "Low energy", "Low libido". Data, not controls. */
export function HrtSummaryChips({ chips }: { chips: string[] }) {
  if (chips.length === 0) return null;
  return (
    <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5" data-testid="hrt-chips">
      {chips.map((chip) => (
        <span key={chip} title={chip} className="max-w-full truncate whitespace-nowrap rounded-[5px] bg-info-soft px-[7px] py-[5px] text-[11.5px] leading-none text-navy">
          {chip}
        </span>
      ))}
    </div>
  );
}

/** The top-right box for a hormone patient. */
export function HrtStatusCard({
  view,
  onOpenLabs,
  onOpenCheckins,
}: {
  view: HrtPatientView;
  onOpenLabs: () => void;
  onOpenCheckins: () => void;
}) {
  const { status, checkins } = view;
  const started = status.programme === 'started';
  const next = status.nextMonitoring;

  const rows: Array<{ label: string; value: ReactNode; title?: string; muted?: boolean }> = [
    {
      label: 'Programme',
      value: started && status.startedOn ? `Started ${shortDay(status.startedOn)}` : status.programmeLabel,
      title: status.usesTestosteroneNow ? 'The intake says the patient uses testosterone now; no start is recorded in MCOS.' : undefined,
    },
    { label: 'Track', value: status.trackLabel ?? 'Not known yet', muted: !status.trackLabel },
    started
      ? {
          label: 'Next labs',
          value: next ? `${next.when}${dueWindow(next) ? `, ${dueWindow(next)}` : ''}` : 'None scheduled',
          title: next ? `${next.id}: due window from the therapy start` : undefined,
          muted: !next,
        }
      : { label: 'First labs', value: status.firstLabs ?? 'Set by the pathway', muted: !status.firstLabs },
    {
      label: 'Check-ins',
      value: checkins.last ? `${status.checkins}, last ${shortDay(checkins.last.submittedAt)}` : status.checkins,
    },
  ];

  return (
    <div className="h-full rounded-[10px] border border-hairline bg-white px-3.5 py-2.5" data-testid="hrt-status-card">
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-mint text-forest">
          <IconCalendar size={17} />
        </span>
        <p className="text-[15px] text-navy">Patient status</p>
      </div>
      <dl className="mt-1.5 space-y-[3px] text-[13px] leading-[18px]">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-ink-muted">{row.label}</dt>
            <dd title={row.title ?? (typeof row.value === 'string' ? row.value : undefined)} className={`min-w-0 truncate text-right ${row.muted ? 'text-ink-muted' : 'font-medium text-navy'}`}>
              {row.value}
            </dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3">
          <dt className="shrink-0 text-ink-muted">Lab alerts</dt>
          <dd className="min-w-0 text-right">
            <button
              type="button"
              onClick={onOpenLabs}
              className={`underline-offset-2 hover:underline ${status.labAlertsOpen > 0 ? 'font-semibold text-alert' : 'font-medium text-navy'}`}
            >
              {status.labAlertsOpen > 0 ? `${status.labAlertsOpen} open` : 'None open'}
            </button>
          </dd>
        </div>
        {status.openAlerts > 0 && (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-ink-muted">Check-in alerts</dt>
            <dd className="min-w-0 text-right">
              <button type="button" onClick={onOpenCheckins} className="font-semibold text-alert underline-offset-2 hover:underline" data-testid="status-checkin-alerts">
                {status.openAlerts} open
              </button>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
